import { useMemo } from 'react'
import type { AdminMeetingRow, AdminMeetingSortBy } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminMeetings } from '@/services/admin'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminDateRange from './AdminDateRange'
import AdminSearchInput from './AdminSearchInput'
import {
  dateInputToEndMs,
  dateInputToStartMs,
  formatAdminMeetingDuration,
  formatDateTime,
} from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface MeetingsTabProps {
  enabled: boolean
  /**
   * Date range, owned by the page. Controlled rather than local so the header's
   * Export button narrows the workbook to the same period the table is showing
   * — a filter that only ever changed the screen was the thing being fixed.
   */
  dateFrom: string
  dateTo: string
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
}

interface MeetingFilters {
  dateFrom?: number
  dateTo?: number
}

/**
 * Meetings tab: paginated meetings with owner info, ratings, search and a date
 * range filter.
 */
export default function MeetingsTab({
  enabled,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: MeetingsTabProps) {
  const { t, locale } = useUiStrings()

  const filters = useMemo<MeetingFilters>(
    () => ({
      dateFrom: dateInputToStartMs(dateFrom),
      dateTo: dateInputToEndMs(dateTo),
    }),
    [dateFrom, dateTo]
  )

  const table = useAdminTable<AdminMeetingRow, AdminMeetingSortBy, MeetingFilters>({
    fetcher: fetchAdminMeetings,
    defaultSortBy: 'createdAt',
    filters,
    enabled,
  })

  const columns: AdminColumn<AdminMeetingRow, AdminMeetingSortBy>[] = [
    { key: 'id', label: t.adminColMeetingId, render: (r) => r.id },
    {
      key: 'title',
      label: t.adminColTitle,
      sortBy: 'title',
      cellClassName: 'max-w-80 min-w-0 whitespace-normal',
      render: (r) => <span className="block min-w-0 break-words">{r.title}</span>,
    },
    {
      key: 'backlogProjectKey',
      sortBy: 'backlogProjectKey',
      // `adminColProject`, already carried for the activity drawer's own
      // Project column — one label for one thing rather than a second string
      // saying the same word.
      label: t.adminColProject,
      // The key as the meeting stored it. There is no name to put beside it:
      // this is a Backlog project key, and the `Project` table is a separate
      // register keyed by `projectCode`, with nothing joining the two.
      render: (r) => r.backlogProjectKey ?? '—',
    },
    { key: 'email', label: t.adminColEmail, sortBy: 'email', render: (r) => r.email },
    { key: 'name', label: t.adminColName, render: (r) => r.name },
    {
      key: 'durationSeconds',
      label: t.adminColDuration,
      sortBy: 'durationSeconds',
      align: 'right',
      render: (r) => formatAdminMeetingDuration(r.durationSeconds),
    },
    {
      key: 'feedbackRating',
      label: t.adminColRating,
      sortBy: 'feedbackRating',
      align: 'right',
      render: (r) => r.feedbackRating ?? '—',
    },
    {
      key: 'feedbackComment',
      label: t.adminColComment,
      render: (r) => (
        <span className="block max-w-xs truncate" title={r.feedbackComment ?? ''}>
          {r.feedbackComment ?? '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t.adminColCreatedAt,
      sortBy: 'createdAt',
      render: (r) => formatDateTime(r.createdAt, locale),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <AdminSearchInput
          value={table.search}
          placeholder={t.adminSearchMeetings}
          onChange={table.setSearch}
        />
        <AdminDateRange
          from={dateFrom}
          to={dateTo}
          onFromChange={onDateFromChange}
          onToChange={onDateToChange}
        />
      </div>
      <AdminDataTable
        columns={columns}
        rows={table.rows}
        rowKey={(r) => r.id}
        loading={table.loading}
        error={table.error}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onToggleSort={table.toggleSort}
        page={table.page}
        totalPages={table.totalPages}
        total={table.total}
        onPageChange={table.setPage}
      />
    </div>
  )
}
